/**
 * The minimap and the world map: the DOM around the map view.
 *
 * Styled like the rest of the game — warm cream panels, slate text, soft
 * shadows, rounded corners — so the map reads as part of the diorama, not as
 * a web page pasted on top of it.
 */

import type { Weather } from '../core/weather/weather.js';
import { accuracyText, locate } from './geolocate.js';
import { drawWeatherSymbol, temperatureColour } from './icons.js';
import type { MapFrame, MapLayers } from './mapView.js';
import { MapView } from './mapView.js';
import type { MapTiles } from './mapTiles.js';
import type { Radar } from './radar.js';
import type { WeatherSamples } from './weatherSamples.js';

const CSS = `
.wsg-panel { background: #f7f0e3; color: #2b2f36; border-radius: 14px;
  box-shadow: 0 6px 18px rgba(30, 28, 40, 0.28); font: 600 13px system-ui, sans-serif; }
.wsg-minimap { position: fixed; right: 16px; bottom: 16px; width: 224px; z-index: 12;
  padding: 5px; cursor: pointer; user-select: none; }
.wsg-minimap .frame { position: relative; width: 214px; height: 214px; border-radius: 11px; overflow: hidden; }
.wsg-minimap .north { position: absolute; top: 6px; left: 50%; transform: translateX(-50%);
  background: #f7f0e3; border-radius: 9px; padding: 0 7px; font-size: 11px; font-weight: 800; }
.wsg-minimap .bar { display: flex; align-items: center; justify-content: space-between; padding: 5px 6px 2px; }
.wsg-minimap .hint { font-size: 11px; color: #6b6f78; }
.wsg-worldmap { position: fixed; inset: 0; z-index: 20; background: #6ec3dc; }
.wsg-worldmap canvas { position: absolute; inset: 0; cursor: grab; }
.wsg-worldmap canvas.dragging { cursor: grabbing; }
.wsg-worldmap .top { position: absolute; top: 14px; left: 14px; padding: 10px 14px; display: flex; gap: 14px; align-items: center; }
.wsg-worldmap .top h1 { margin: 0; font: 800 17px system-ui, sans-serif; }
.wsg-worldmap .chip { border: 0; border-radius: 999px; padding: 5px 11px; background: #e6dcc9; color: #4a4f58;
  font: 700 12px system-ui, sans-serif; cursor: pointer; }
.wsg-worldmap .chip.on { background: #2f6fb0; color: #fff; }
.wsg-worldmap .close { position: absolute; top: 14px; right: 14px; width: 40px; height: 40px; border: 0;
  font: 800 18px system-ui; cursor: pointer; }
.wsg-worldmap .legend { position: absolute; left: 14px; bottom: 14px; padding: 10px 14px; font-size: 11px; }
.wsg-worldmap .scale { display: flex; height: 10px; width: 220px; border-radius: 5px; overflow: hidden; margin: 4px 0 8px; }
.wsg-worldmap .credit { position: absolute; right: 14px; bottom: 14px; padding: 6px 10px; font: 500 11px system-ui; color: #5a5f68; }
.wsg-worldmap .popup { position: absolute; padding: 10px 12px; transform: translate(-50%, calc(-100% - 44px));
  min-width: 190px; text-align: center; }
.wsg-worldmap .popup button { margin-top: 8px; border: 0; border-radius: 10px; padding: 8px 14px; width: 100%;
  background: #e2473a; color: #fff; font: 800 14px system-ui; cursor: pointer; }
.wsg-worldmap .popup .where { font: 500 11px ui-monospace, monospace; color: #6b6f78; }
.wsg-worldmap .hint { position: absolute; left: 50%; bottom: 16px; transform: translateX(-50%); padding: 6px 12px; font-size: 12px; }
.wsg-worldmap .chip.locate { background: #e2473a; color: #fff; }
.wsg-worldmap .chip.locate:disabled { opacity: 0.7; cursor: progress; }
.wsg-worldmap .toast { position: absolute; left: 50%; top: 50%; transform: translate(-50%, -50%); max-width: min(360px, 80vw);
  padding: 12px 16px; text-align: center; }
.wsg-minimap .locate { position: absolute; top: 6px; left: 6px; width: 30px; height: 30px; border: 0; border-radius: 50%;
  padding: 0; background: #f7f0e3; font-size: 15px; cursor: pointer; box-shadow: 0 2px 6px rgba(30, 28, 40, 0.25); }
.wsg-worldmap .hint .touch { display: none; }
html.wsg-touch .wsg-worldmap .hint .mouse { display: none; }
html.wsg-touch .wsg-worldmap .hint .touch { display: inline; }
@media (max-width: 720px) {
  .wsg-worldmap .top { top: max(10px, env(safe-area-inset-top)); left: 10px; right: 62px; flex-wrap: wrap; gap: 6px 8px; padding: 8px 10px; }
  .wsg-worldmap .top h1 { font-size: 15px; width: 100%; }
  .wsg-worldmap .close { top: max(10px, env(safe-area-inset-top)); right: 10px; }
  .wsg-worldmap .legend { display: none; }
  .wsg-worldmap .credit { right: 0; bottom: 0; border-radius: 8px 0 0 0; font-size: 9px; padding: 3px 8px; }
  .wsg-worldmap .hint { bottom: max(28px, calc(env(safe-area-inset-bottom) + 22px)); white-space: nowrap; font-size: 11px; }
}
`;

function injectStyle(): void {
  if (document.getElementById('wsg-map-style') !== null) return;
  const style = document.createElement('style');
  style.id = 'wsg-map-style';
  style.textContent = CSS;
  document.head.appendChild(style);
}

export interface MapServices {
  tiles: MapTiles;
  radar: Radar;
  samples: WeatherSamples;
}

/** The corner map: always centred on the player, north up. */
export class Minimap {
  readonly root = document.createElement('div');
  private readonly view: MapView;
  private readonly status = document.createElement('canvas');
  private readonly time = document.createElement('span');
  private lastDraw = 0;
  visible = true;

  constructor(parent: HTMLElement, services: MapServices, onOpen: () => void, onLocate?: () => void) {
    injectStyle();
    this.root.className = 'wsg-panel wsg-minimap';
    const frame = document.createElement('div');
    frame.className = 'frame';
    const canvas = document.createElement('canvas');
    frame.appendChild(canvas);
    const north = document.createElement('div');
    north.className = 'north';
    north.textContent = 'N';
    frame.appendChild(north);
    if (onLocate !== undefined) {
      const locateButton = document.createElement('button');
      locateButton.className = 'locate';
      locateButton.textContent = '📍';
      locateButton.title = 'Go to my location (L)';
      locateButton.addEventListener('click', (e) => {
        e.stopPropagation();
        onLocate();
      });
      frame.appendChild(locateButton);
    }
    this.root.appendChild(frame);

    const bar = document.createElement('div');
    bar.className = 'bar';
    this.status.width = 96;
    this.status.height = 40;
    this.status.style.width = '48px';
    this.status.style.height = '20px';
    const hint = document.createElement('span');
    hint.className = 'hint';
    hint.textContent = 'N · world map';
    bar.append(this.time, this.status, hint);
    this.root.appendChild(bar);
    parent.appendChild(this.root);

    this.view = new MapView(canvas, services.tiles, services.radar, services.samples);
    this.view.resize(214, 214);
    this.view.zoom = 14.6;
    this.root.addEventListener('click', onOpen);
  }

  /** Change the map's size in CSS pixels; the touch layout uses a smaller one. */
  resize(size: number): void {
    this.view.resize(size, size);
    this.lastDraw = 0;
  }

  toggle(): void {
    this.visible = !this.visible;
    this.root.style.display = this.visible ? '' : 'none';
  }

  zoomBy(delta: number): void {
    this.view.zoom = Math.max(9, Math.min(16.5, this.view.zoom + delta));
  }

  update(frame: MapFrame, weather: Weather, clock: string, night: boolean): void {
    if (!this.visible) return;
    const now = performance.now();
    if (now - this.lastDraw < 66) return;
    this.lastDraw = now;
    this.view.lat = frame.player.lat;
    this.view.lon = frame.player.lon;
    this.view.draw(frame);
    this.time.textContent = clock;
    const ctx = this.status.getContext('2d')!;
    ctx.setTransform(2, 0, 0, 2, 0, 0);
    ctx.clearRect(0, 0, 48, 20);
    drawWeatherSymbol(ctx, 10, 11, 16, weather.code, weather.cloud, night);
    ctx.font = '800 11px system-ui';
    ctx.fillStyle = temperatureColour(weather.temperature);
    ctx.textAlign = 'left';
    ctx.textBaseline = 'middle';
    ctx.fillText(`${Math.round(weather.temperature)}°`, 24, 11);
  }
}

/** The world map's zoom when opened: the streets round the player. */
const CLOSE_ZOOM = 15;
/** ... and stepped out to with Z: the whole world. */
const WORLD_ZOOM = 2.5;

/** Travelling to a chosen point: what the button says, and the going. */
export interface MapTravel {
  /** "🚗 Drive there", or undefined when there is no going from here. */
  label(): string | undefined;
  go(lat: number, lon: number): void;
}

/** The fullscreen world map: pan, zoom, and pick somewhere to jump to. */
export class WorldMap {
  /** Set by the game: the second choice for a chosen point, besides parachuting there. */
  travel: MapTravel | undefined;
  private readonly worldChip = document.createElement('button');
  readonly root = document.createElement('div');
  private readonly view: MapView;
  private readonly canvas = document.createElement('canvas');
  private readonly popup = document.createElement('div');
  private readonly clockText = document.createElement('span');
  private target: { lat: number; lon: number } | undefined;
  private readonly locateButton = document.createElement('button');
  private readonly toast = document.createElement('div');
  private toastTimer: number | undefined;
  private readonly layers: MapLayers = { night: true, radar: true, weather: true, labels: true };
  private readonly people = document.createElement('button');
  open = false;

  constructor(
    parent: HTMLElement,
    services: MapServices,
    private readonly onJump: (lat: number, lon: number) => void,
    /** However the map closes — ✕, Esc or a jump — so the game can take its input back. */
    private readonly onClose: () => void = () => {},
  ) {
    injectStyle();
    this.root.className = 'wsg-worldmap';
    this.root.style.display = 'none';
    this.root.appendChild(this.canvas);

    const top = document.createElement('div');
    top.className = 'wsg-panel top';
    const title = document.createElement('h1');
    title.textContent = 'World map';
    top.append(title, this.clockText);
    for (const [key, name] of [['weather', 'Weather'], ['radar', 'Radar'], ['night', 'Day & night'], ['labels', 'Names']] as const) {
      const chip = document.createElement('button');
      chip.className = 'chip on';
      chip.textContent = name;
      chip.addEventListener('click', () => {
        this.layers[key] = !this.layers[key];
        chip.classList.toggle('on', this.layers[key]);
      });
      top.appendChild(chip);
    }
    this.locateButton.className = 'chip locate';
    this.locateButton.textContent = '📍 My location';
    this.locateButton.title = 'Find where you are (L)';
    this.locateButton.addEventListener('click', () => this.locateMe());
    top.appendChild(this.locateButton);
    this.people.className = 'chip';
    this.people.style.display = 'none';
    top.appendChild(this.people);
    this.worldChip.className = 'chip';
    this.worldChip.textContent = '🌍 World (Z)';
    this.worldChip.title = 'Step out to the whole world, or back in to where you are (Z)';
    this.worldChip.addEventListener('click', () => this.toggleWorld());
    top.appendChild(this.worldChip);
    window.addEventListener('keydown', (e) => {
      if (this.open && e.code === 'KeyZ') this.toggleWorld();
    });
    this.root.appendChild(top);

    const close = document.createElement('button');
    close.className = 'wsg-panel close';
    close.textContent = '✕';
    close.title = 'Close (Esc)';
    close.addEventListener('click', () => this.hide());
    this.root.appendChild(close);

    const legend = document.createElement('div');
    legend.className = 'wsg-panel legend';
    const temps = [-20, -10, 0, 10, 20, 30, 40];
    legend.innerHTML = `<div>Temperature °C</div><div class="scale">${temps.map(t => `<span style="flex:1;background:${temperatureColour(t)}"></span>`).join('')}</div>`
      + `<div style="display:flex;justify-content:space-between;width:220px">${temps.map(t => `<span>${t}</span>`).join('')}</div>`
      + `<div style="margin-top:8px">Rain radar</div><div class="scale">${['#96d4ff', '#4696eb', '#2454cd', '#fac83c', '#e44032'].map(c => `<span style="flex:1;background:${c}"></span>`).join('')}</div>`
      + `<div style="display:flex;justify-content:space-between;width:220px"><span>light</span><span>heavy</span></div>`;
    this.root.appendChild(legend);

    const credit = document.createElement('div');
    credit.className = 'wsg-panel credit';
    credit.textContent = '© OpenStreetMap contributors · Elevation: Mapzen · Weather: Open-Meteo · Radar: RainViewer';
    this.root.appendChild(credit);

    const hint = document.createElement('div');
    hint.className = 'wsg-panel hint';
    hint.innerHTML = '<span class="mouse">Drag to pan · scroll to zoom · click to choose a landing spot · double-click to jump</span>'
      + '<span class="touch">Drag to pan · pinch to zoom · tap to choose a landing spot</span>';
    this.root.appendChild(hint);

    this.toast.className = 'wsg-panel toast';
    this.toast.style.display = 'none';
    this.root.appendChild(this.toast);

    this.popup.className = 'wsg-panel popup';
    this.popup.style.display = 'none';
    this.root.appendChild(this.popup);
    parent.appendChild(this.root);

    this.view = new MapView(this.canvas, services.tiles, services.radar, services.samples);
    this.bindInput();
    window.addEventListener('resize', () => { if (this.open) this.view.resize(window.innerWidth, window.innerHeight); });
  }

  show(lat: number, lon: number): void {
    this.open = true;
    this.root.style.display = '';
    this.view.resize(window.innerWidth, window.innerHeight);
    this.view.lat = lat;
    this.view.lon = lon;
    // Opened close in, on the streets round the player; Z steps out to the world.
    this.view.zoom = CLOSE_ZOOM;
    this.home = { lat, lon };
    this.target = undefined;
    this.popup.style.display = 'none';
    this.toast.style.display = 'none';
    if (document.pointerLockElement !== null) document.exitPointerLock();
  }

  /** Out to the whole world, or back in close — to the chosen point, else where the map was. */
  toggleWorld(): void {
    if (this.view.zoom > (CLOSE_ZOOM + WORLD_ZOOM) / 2) {
      this.view.zoom = WORLD_ZOOM;
    } else {
      if (this.target !== undefined) { this.view.lat = this.target.lat; this.view.lon = this.target.lon; }
      else if (this.home !== undefined) { this.view.lat = this.home.lat; this.view.lon = this.home.lon; }
      this.view.zoom = CLOSE_ZOOM;
    }
  }

  /** Where the player was when the map opened, to come back to. */
  private home: { lat: number; lon: number } | undefined;

  hide(): void {
    if (!this.open) return;
    this.open = false;
    this.root.style.display = 'none';
    this.onClose();
  }

  /**
   * Ask the device where it is, centre the map on it and offer the jump there.
   * The player still confirms with "Parachute here": a desktop's network fix
   * can be kilometres out, and the accuracy is on the popup to judge it by.
   */
  async locateMe(): Promise<void> {
    if (this.locateButton.disabled) return;
    this.locateButton.disabled = true;
    this.locateButton.textContent = '📍 Locating…';
    try {
      const fix = await locate();
      if (!this.open) return;
      this.view.lat = fix.lat;
      this.view.lon = fix.lon;
      // Zoomed to how much the fix can be trusted: street level for GPS, the
      // town for Wi-Fi, the region for an IP guess.
      this.view.zoom = fix.accuracy < 250 ? 14.5 : fix.accuracy < 3000 ? 12 : 9.5;
      this.choose(fix.lat, fix.lon, `You are here · ${accuracyText(fix.accuracy)}`);
    } catch (e) {
      this.say(e instanceof Error ? e.message : String(e));
    } finally {
      this.locateButton.disabled = false;
      this.locateButton.textContent = '📍 My location';
    }
  }

  private say(message: string, ms = 5000): void {
    this.toast.textContent = message;
    this.toast.style.display = '';
    window.clearTimeout(this.toastTimer);
    this.toastTimer = window.setTimeout(() => { this.toast.style.display = 'none'; }, ms);
  }

  /** Open on the whole region, asking the player to choose where to start. */
  askForStart(lat: number, lon: number): void {
    this.show(lat, lon);
    this.view.zoom = 4.5;
    this.say('Where would you like to start? Tap a spot on the map, then jump.', 9000);
  }

  private choose(lat: number, lon: number, label?: string): void {
    this.target = { lat, lon };
    this.popup.innerHTML = '';
    const where = document.createElement('div');
    where.className = 'where';
    where.textContent = label ?? `${lat.toFixed(4)}, ${lon.toFixed(4)}`;
    const go = document.createElement('button');
    go.textContent = '⬇  Parachute here';
    go.addEventListener('click', (e) => {
      e.stopPropagation();
      this.jump(lat, lon);
    });
    this.popup.append(where, go);
    // Or go there the way the player is going: drive, run, ride or fly.
    const going = this.travel?.label();
    if (going !== undefined) {
      const travel = document.createElement('button');
      travel.textContent = going;
      travel.addEventListener('click', (e) => {
        e.stopPropagation();
        this.hide();
        this.travel?.go(lat, lon);
      });
      this.popup.appendChild(travel);
    }
    this.popup.style.display = '';
  }

  private jump(lat: number, lon: number): void {
    this.hide();
    this.onJump(lat, lon);
  }

  private bindInput(): void {
    // Every finger on the map, so two of them can pinch.
    const pointers = new Map<number, { x: number; y: number }>();
    let down: { x: number; y: number; moved: boolean } | undefined;
    let pinch: number | undefined;
    const spread = () => {
      const [a, b] = [...pointers.values()];
      return { d: Math.hypot(a.x - b.x, a.y - b.y), x: (a.x + b.x) / 2, y: (a.y + b.y) / 2 };
    };
    this.canvas.addEventListener('pointerdown', (e) => {
      pointers.set(e.pointerId, { x: e.clientX, y: e.clientY });
      this.canvas.setPointerCapture(e.pointerId);
      if (pointers.size === 2) {
        // A pinch is never a tap, and the lone-finger pan stops while it lasts.
        pinch = spread().d;
        down = undefined;
      } else if (pointers.size === 1) {
        down = { x: e.clientX, y: e.clientY, moved: false };
      }
    });
    this.canvas.addEventListener('pointermove', (e) => {
      const p = pointers.get(e.pointerId);
      if (p === undefined) return;
      p.x = e.clientX;
      p.y = e.clientY;
      if (pinch !== undefined && pointers.size >= 2) {
        const s = spread();
        if (s.d > 0 && pinch > 0) this.view.zoomAt(s.x, s.y, Math.log2(s.d / pinch));
        pinch = s.d;
        return;
      }
      if (down === undefined) return;
      const dx = e.clientX - down.x, dy = e.clientY - down.y;
      if (!down.moved && Math.hypot(dx, dy) < 6) return;
      down.moved = true;
      this.canvas.classList.add('dragging');
      this.view.panBy(dx, dy);
      down.x = e.clientX;
      down.y = e.clientY;
    });
    const end = (e: PointerEvent, cancelled: boolean) => {
      if (!pointers.delete(e.pointerId)) return;
      this.canvas.classList.remove('dragging');
      if (!cancelled && pinch === undefined && down !== undefined && !down.moved) {
        const p = this.view.toLatLon(e.clientX, e.clientY);
        this.choose(p.lat, p.lon);
      }
      if (pointers.size < 2) pinch = undefined;
      down = undefined;
    };
    this.canvas.addEventListener('pointerup', (e) => end(e, false));
    this.canvas.addEventListener('pointercancel', (e) => end(e, true));
    this.canvas.addEventListener('dblclick', (e) => {
      const p = this.view.toLatLon(e.clientX, e.clientY);
      this.jump(p.lat, p.lon);
    });
    this.canvas.addEventListener('wheel', (e) => {
      e.preventDefault();
      this.view.zoomAt(e.clientX, e.clientY, -Math.sign(e.deltaY) * 0.35);
    }, { passive: false });
  }

  /**
   * Show who is online, and whether the player is shown to them: a chip that
   * toggles it. Hidden while there is no room to be in.
   */
  setPeople(state: { connected: boolean; online: number; visible: boolean }, toggle: () => void): void {
    this.people.style.display = state.connected ? '' : 'none';
    this.people.classList.toggle('on', state.visible);
    const others = Math.max(0, state.online - 1);
    const shown = !state.visible ? 'you are hidden' : 'you are visible';
    this.people.textContent = `👥 ${others} other${others === 1 ? '' : 's'} online · ${shown}`;
    this.people.title = 'Show or hide yourself to other players';
    this.people.onclick = toggle;
  }

  update(frame: MapFrame, clock: string): void {
    if (!this.open) return;
    this.clockText.textContent = clock;
    this.view.draw({ ...frame, layers: this.layers, target: this.target, bodies: true, names: true });
    if (this.target !== undefined) {
      const [x, y] = this.view.toScreen(this.target.lat, this.target.lon);
      this.popup.style.left = `${x}px`;
      this.popup.style.top = `${y}px`;
    }
  }
}

